import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const Pricing = () => {
  const { t } = useLanguage();
  
  const features = [
    t('feature_listings'),
    t('feature_photo'),
    'AI Virtual Staging for empty rooms',
    t('feature_mls'),
    t('feature_social'),
    t('feature_email'),
    t('feature_sms'),
    t('feature_bilingual'),
    t('feature_copy'),
    'Market data insights & trends',
    'AI market update generator',
    'Contact management & CSV import',
    'Agent profile & branding',
    t('feature_updates'),
    t('feature_export'),
    t('feature_pdf'),
    t('feature_batch'),
    t('feature_share'),
    t('feature_support')
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="hero-gradient pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles size={16} className="text-blue-400" />
            {t('pricing_badge')}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
            {t('pricing_title_1')} <span className="text-gradient">{t('pricing_title_2')}</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            One simple price. Pay once, own forever. Unlimited access to all features.
          </p>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="py-20 -mt-10 relative z-10">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
          <Card 
            className="relative overflow-hidden border-0 shadow-xl feature-card ring-2 ring-blue-500"
            data-testid="pricing-pro"
          >
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-center py-2 text-sm font-semibold">
              Best Value
            </div>
            <CardHeader className="text-center pb-4 pt-12">
              <CardTitle className="text-2xl font-heading">{t('pricing_plan_name')}</CardTitle>
              <CardDescription className="text-muted-foreground">{t('pricing_plan_desc')}</CardDescription>
              <div className="mt-6">
                <span className="text-5xl font-heading font-bold text-foreground">$20</span>
                <span className="text-muted-foreground ml-2">one-time</span>
              </div>
              <p className="text-sm text-green-600 font-medium mt-2">Lifetime access - no recurring fees!</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup">
                <Button 
                  className="w-full rounded-full py-6 btn-primary-gradient text-white border-0"
                  data-testid="select-pro-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <p className="text-center text-xs text-muted-foreground mt-4">
                Secure payment via Stripe
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            {t('pricing_faq_title')}
          </h2>
          <p className="text-muted-foreground mb-8">
            {t('pricing_faq_subtitle')}
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/faq">
              <Button variant="outline" className="rounded-full px-6">{t('pricing_view_faq')}</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full px-6">{t('pricing_contact')}</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
